import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import { useId } from "react";
import cx from "ui/cx";
import IconBuster from "ui/icons/IconBuster";
import IconDash from "ui/icons/IconDash";
import IconLocked from "ui/icons/IconLocked";
import styles from "./CheckboxOld.module.scss";

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onClick"> {
  labelStyles?: CSSProperties;
  label?: ReactNode;
  iconLock?: boolean;
  controlled?: boolean; // check/uncheck handled by parent
  readOnly?: boolean;
  mark?: "check" | "partialCheck";
  onClick?: MouseEventHandler<HTMLLabelElement>; // onClick needs to live on label to work properly
  indeterminate?: boolean;
  previewing?: boolean;
  iconClassName?: string;
  forbidden?: boolean;
}

export default function Checkbox({
  label,
  labelStyles = {},
  className,
  mark = "check",
  onClick,
  indeterminate = false,
  previewing = false,
  iconClassName,
  forbidden = false,
  ...props
}: CheckboxProps) {
  const { disabled, iconLock, controlled, readOnly, onChange = undefined, checked, ...inputProps } = props;

  const id = useId();

  return (
    <div
      className={cx(
        styles.wrapper,
        "checkbox",
        { styles, disabled, iconLock, readOnly, indeterminate },
        styles[mark],
        className,
      )}
      style={props.style}
      title={inputProps.title}
    >
      <input
        className={styles.input}
        type="checkbox"
        checked={!iconLock && checked}
        {...inputProps}
        id={id}
        onChange={!controlled || !readOnly ? onChange : () => null}
        disabled={disabled || iconLock || controlled || readOnly}
      />
      <label className={cx(styles.icon, "icon", iconClassName)} htmlFor={id} onClick={onClick}>
        {iconLock && <IconLocked variant="portal" style={{ marginBottom: "1px" }} color="stone" size={10} />}
        {indeterminate && <IconDash color="sapphire" style={{ width: "70%" }} />}
        {forbidden && <IconBuster style={{ width: "70%" }} />}
      </label>
      {label && (
        <label className={cx(styles.label, (disabled || iconLock) && styles.disabled)} htmlFor={id} style={labelStyles}>
          {label}
        </label>
      )}
    </div>
  );
}
